/* CHRISTMAS ON THE PORTAL (Max, 31/10/2026: "sắp tới christmas tôi muốn làm UI cho mode này" — good UI/UX, contrast, readable,
   up to WCAG). What he chose, asked one by one:
     - it switches ITSELF ON for the season, 01/Dec → 05/Jan, for everybody, and anybody can switch it off for themselves
       (remembered in the browser, for this season only); an Admin moves the dates with the setting ui.christmas_window
       ("12-01..01-05", or "off" / "on");
     - light decoration plus snow falling in the HEADER only — the snow stops for anybody whose device asks for reduced motion,
       and it has its own off switch (WCAG 2.2.2: moving content can be paused);
     - only the frame (header, sidebar), Home and the login page — the data screens are left exactly as they are.

   LIKE THE DARK THEME, IT IS ONE ATTRIBUTE: <html data-season="christmas" data-snow="on|off">, set by the inline script in
   public/index.html before the first paint (so nothing flashes) and kept up to date here. Every colour is static CSS in
   theme-christmas.css; nothing here touches data loading.

   `?season=christmas` in the address shows it out of season for this tab (to check it before December); `?season=off`
   ends that preview. */
import { useCallback, useEffect, useState } from 'react';

export const XMAS_DEFAULT = '12-01..01-05';
const KEY = { win: 'portalXmasWindow', off: 'portalXmasOff', snowOff: 'portalXmasSnowOff', preview: 'portalXmasPreview' };
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set: (k, v) => { try { if (v === null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch (e) { /* private window */ } },
};
const session = {
  get: (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
  set: (k, v) => { try { if (v === null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch (e) { /* private window */ } },
};

/* the window wraps the new year: 12-01..01-05 holds 20/Dec and 03/Jan */
const mmdd = (d) => `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const inWindow = (win, d = new Date()) => {
  const w = String(win || XMAS_DEFAULT).trim().toLowerCase();
  if (w === 'on') return true;
  if (w === 'off') return false;
  const m = /^(\d{2}-\d{2})\.\.(\d{2}-\d{2})$/.exec(w);
  if (!m) return inWindow(XMAS_DEFAULT, d);
  const t = mmdd(d);
  return m[1] <= m[2] ? (t >= m[1] && t <= m[2]) : (t >= m[1] || t <= m[2]);
};
/* the season a date belongs to — a January day belongs to last December's, so "off" in December still holds on 2/Jan */
const seasonOf = (d = new Date()) => String(d.getMonth() === 0 ? d.getFullYear() - 1 : d.getFullYear());

export const readSeason = () => {
  const preview = session.get(KEY.preview) === '1';
  const available = preview || inWindow(store.get(KEY.win) || XMAS_DEFAULT);
  const on = available && store.get(KEY.off) !== seasonOf();
  return { available, preview, on, snow: on && store.get(KEY.snowOff) !== '1' };
};

export const applySeason = (s = readSeason()) => {
  const html = document.documentElement;
  if (s.on) html.dataset.season = 'christmas'; else delete html.dataset.season;
  html.dataset.snow = s.snow ? 'on' : 'off';
  return s;
};

/* the address's ?season= — read once, at start */
const takePreviewParam = () => {
  try {
    const v = new URLSearchParams(window.location.search).get('season');
    if (v === 'christmas') session.set(KEY.preview, '1');
    if (v === 'off') session.set(KEY.preview, null);
  } catch (e) { /* no window */ }
};

/* once, at start (index.js): the preview in the address, the attributes, the Admin's dates (public — the login page shows the
   season too; kept for the next first paint). Home's greeting and the login ribbon are always in the page and shown by CSS
   alone, so only the header's switch needs React state. */
const listeners = new Set();
const announce = () => { const s = applySeason(); listeners.forEach((f) => f(s)); };
export const initSeason = () => {
  takePreviewParam();
  applySeason();
  /* the moving pieces wait for the page to settle (theme-christmas.css: data-season-ready) — the first seconds belong to the
     screen's own data (Max: "trang homepage bị lag", 31/10) */
  const ready = () => { document.documentElement.dataset.seasonReady = '1'; };
  const later = () => (window.requestIdleCallback ? window.requestIdleCallback(ready, { timeout: 2500 }) : setTimeout(ready, 1200));
  if (document.readyState === 'complete') setTimeout(later, 900); else window.addEventListener('load', () => setTimeout(later, 900), { once: true });
  fetch('/api/public/season', { credentials: 'omit' })
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => { if (d && d.window) { store.set(KEY.win, String(d.window)); announce(); } })
    .catch(() => { /* the default window stands */ });
};

export function useSeason() {
  const [s, setS] = useState(readSeason);
  useEffect(() => { listeners.add(setS); return () => { listeners.delete(setS); }; }, []);
  const setOn = useCallback((on) => { store.set(KEY.off, on ? null : seasonOf()); announce(); }, []);
  const setSnow = useCallback((on) => { store.set(KEY.snowOff, on ? null : '1'); announce(); }, []);
  return { ...s, setOn, setSnow };
}
